import { useCallback } from 'react';

import styled, { css } from 'styled-components';

import { UnitInputProps } from './UnitInput.decl';
import { getValidationColor } from './utils';

import { Flex, fromTheme, TextFieldInput } from '@aircall/tractor';

const UnitInputText = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.grey.dark};
  font-size: 14px;
  transition: all 0.3s;
`;

const UnitInputWrapper = styled(Flex)<Omit<UnitInputProps, 'unit'>>`
  display: inline-flex;
  width: ${({ width }) => width};
  align-items: center;
  border-radius: ${({ theme }) => theme.radii[8]};
  ${({ size, disabled, theme }) => css`
    display: flex;
    min-width: 85px;
    height: ${size === 'small' ? '36px' : '48px'};
    flex-direction: row;
    padding-right: ${size === 'small' ? '0' : theme.space.s}px;
    background: ${disabled ? theme.colors.grey.light : theme.colors.base.white};
    color: ${disabled ? theme.colors.text.base : theme.colors.grey.dark};

    > span {
      flex: ${size === 'small' ? '1' : 'unset'};
    }

    > div {
      flex: ${size === 'small' ? '1' : 'unset'};

      > input {
        padding-right: ${size === 'small' ? '0' : '4px'};
        padding-left: 0;
        background: transparent;
        text-align: right;

        &,
        &:focus {
          /**
            * This is bad, but currently there is no other way to
            * override TextInputField from Tractor
            */
          border-color: transparent !important;
          box-shadow: none !important;
        }
      }
    }
  `}

  /**
  * This very convoluted piece of CSS is written to override styling of TextInputField from Tractor
  * It may be a good idea to refactor the component in Tractor by default as right now overriding
  * it is a difficult task and this kind of styling should be avoided.
  *
  * What this essentially does is:
  * - Check if validation status is provided
  * - If yes, apply border properties to the parent to mimic exactly how a TextInput would
  * - If no, then fall back to defaults
  */
  ${({ validationStatus }) => {
    const color = getValidationColor(validationStatus);

    return css`
      border: 1px solid ${fromTheme(color || 'colors.grey.base')};

      &:focus-within {
        border-color: ${fromTheme(color || 'colors.base.black')};
        box-shadow: ${fromTheme(color || 'transparent', 'transparent')} 0 0 0 1px;
      }
    `;
  }}
  transition: all 0.3s;
`;

export function UnitInput({
  unit,
  pullRight,
  width,
  textAlign,
  'data-test': dataTest = 'unit-input',
  validationStatus,
  disabled,
  onChange,
  size,
  ...rest
}: UnitInputProps) {
  /**
   * Forces formatting the input as a number for controlled components,
   * e.g., `01` ➞ `1`
   */
  const onInputChange = useCallback(
    (event) => {
      /* istanbul ignore else  */
      if (onChange) {
        const inputVal = event.target.value;
        onChange({
          ...event,
          target: {
            ...event.target,
            value: Number(inputVal).toString(),
          },
        });
      }
    },
    [onChange]
  );

  return (
    <UnitInputWrapper
      width={width}
      pullRight={pullRight}
      textAlign={textAlign}
      disabled={disabled}
      size={size}
      validationStatus={validationStatus}
      data-test={disabled ? `${dataTest}-disabled` : `${dataTest}`}
    >
      <TextFieldInput
        {...rest}
        onChange={onInputChange}
        disabled={disabled}
        validationStatus={validationStatus}
        data-test={`${dataTest}-input`}
      />
      <UnitInputText data-test={`${dataTest}-${unit}`}>{unit}</UnitInputText>
    </UnitInputWrapper>
  );
}

(UnitInput as React.ComponentType<UnitInputProps>).defaultProps = {
  pullRight: true,
  textAlign: 'left',
  width: '100%',
};
